import { Modal, useFormatting } from "@msys/ui";
import { Box, List, Stack, Typography, useTheme } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { FileListItemButton } from "../../commons/files/FileListItemButton.js";
import { GalleryRow } from "../../commons/images/GalleryRow.js";
import { ClientInput } from "./types.js";
import { useClientInputAttachments } from "./useClientInputAttachments.js";

interface Props {
  clientInput: ClientInput;
  handleClose: () => void;
}

export const ClientInputModal = ({ clientInput, handleClose }: Props) => {
  const { t } = useTranslate(["ProjectOverview", "Global"]);
  const { getFormattedDateTime } = useFormatting();
  const { images, files } = useClientInputAttachments(clientInput);
  const theme = useTheme();

  return (
    <Modal
      title={t("Client input", { ns: "ProjectOverview" })}
      handleClose={handleClose}
      maxWidth="sm"
    >
      <Stack direction="column" spacing={2}>
        <Typography variant="caption" color="text.secondary">
          {getFormattedDateTime(new Date(clientInput.createdAt))}
        </Typography>

        {clientInput.commentText && (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {clientInput.commentText}
          </Typography>
        )}

        {images.length > 0 && (
          // Every picture of the input as one thumbnail row — no inline
          // preview. Clicking a thumbnail opens the full-screen viewer at that
          // picture, from where the rest can be paged through.
          <Box sx={{ overflowX: "auto" }}>
            <GalleryRow
              images={images}
              imageHeight={theme.layout.imageSize.xl}
            />
          </Box>
        )}

        {files.length > 0 && (
          <List dense disablePadding>
            {files.map(file => (
              <FileListItemButton
                key={file.id}
                file={{ url: file.url, title: file.title || file.url }}
              />
            ))}
          </List>
        )}
      </Stack>
    </Modal>
  );
};
